// src/components/pages/b2b/ESG/EsgContentPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  Link as MLink,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import EsgMissionVision from "./EsgMissionVision";
import EsgPillar from "./EsgPillar";
import EsgStrategy from "./EsgStrategy";

export const slugify = (s) =>
  String(s || "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[()（）]/g, "");

export const MENU_ITEMS = [
  {
    key: "mission-vision",
    sectionTitle: {
      ZH: "我們的使命與願景",
      EN: "Mission and Vision",
      JP: "私たちの使命とビジョン",
    },
    Component: EsgMissionVision,
  },
  {
    key: "esg-pillars",
    sectionTitle: {
      ZH: "ESG 三大主軸",
      EN: "ESG: Three Pillars",
      JP: "ESGの三つの柱",
    },
    Component: EsgPillar,
  },
  {
    key: "esg-strategy",
    sectionTitle: {
      ZH: "ESG 永續策略",
      EN: "ESG Sustainability Strategy",
      JP: "ESGサステナビリティ戦略",
    },
    Component: EsgStrategy,
  },
];

export const VISIBLE_MENU_ITEMS = MENU_ITEMS;

function getLocalizedSectionTitle(item, lang) {
  return item.sectionTitle?.[lang] || item.sectionTitle?.ZH || "";
}

export default function EsgContentPage({
  embedded = false,
  itemSlug: propSlug,
}) {
  const params = useParams();

  const [lang, setLang] = useState(() => getLangFromCookie() || "ZH");

  const fonts = {
    EN: "LINE Seed Sans",
    ZH: "LINE Seed TW",
    JP: "LINE Seed JP",
  };

  useEffect(() => {
    const syncLang = () => setLang(getLangFromCookie() || "ZH");

    window.addEventListener("app:languageChanged", syncLang);

    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  const effectiveSlug = propSlug ?? params?.itemSlug;

  const active = useMemo(() => {
    if (!effectiveSlug) {
      return MENU_ITEMS[0];
    }

    const fromRoute = decodeURIComponent(effectiveSlug);

    return (
      MENU_ITEMS.find(
        (item) =>
          slugify(getLocalizedSectionTitle(item, "ZH")) === fromRoute,
      ) || MENU_ITEMS[0]
    );
  }, [effectiveSlug]);

  const ActiveComponent = active.Component || EsgMissionVision;

  const sectionTitle = getLocalizedSectionTitle(active, lang);

  const showBreadcrumb = !embedded;

  const CR_LABEL =
    lang === "EN"
      ? "Corporate Responsibility"
      : lang === "JP"
        ? "企業の社会的責任"
        : "企業責任";

  return (
    <Box
      component="section"
      id={embedded ? "esg-content-section-inner" : undefined}
      sx={{
        pt: embedded
          ? {
              xs: 3,
              md: 4,
            }
          : {
              xs: 4,
              md: 6,
            },

        pb: 0,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: {
            xs: "100%",
            xl: 1540,
          },

          px: {
            xs: 2,
            md: 4,
          },

          mx: "auto",
        }}
      >
        {showBreadcrumb && (
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              mb: {
                xs: 2.5,
                md: 3.5,
              },
            }}
          >
            <MLink
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/企業責任"
            >
              {CR_LABEL}
            </MLink>

            <Typography color="text.primary">{sectionTitle}</Typography>
          </Breadcrumbs>
        )}

        <ActiveComponent lang={lang} fonts={fonts} />
      </Container>
    </Box>
  );
}